import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/state/AuthContext';
import { useToasts } from '@/state/ToastContext';
import { notificationsBaseQueryKey } from '@/services/api';

export const useNotificationStream = () => {
  const { isAuthenticated, token } = useAuth();
  const { pushToast } = useToasts();
  const queryClient = useQueryClient();
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !isAuthenticated || !token) return undefined;
    let stopped = false;
    let ws: WebSocket | null = null;
    let retry = 0;

    const cleanup = () => {
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    const connect = () => {
      if (stopped) return;
      const origin = new URL(window.location.origin);
      origin.protocol = origin.protocol === 'https:' ? 'wss:' : 'ws:';
      origin.pathname = '/api/notifications/live';
      origin.searchParams.set('token', token);
      ws = new WebSocket(origin.toString());
      ws.onopen = () => {
        retry = 0;
      };
      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === 'care-notification') {
            queryClient.invalidateQueries({ queryKey: notificationsBaseQueryKey });
            if (payload?.record?.message) {
              pushToast({ tone: 'info', message: payload.record.message });
            }
          }
        } catch (err) {
          console.warn('Failed to parse realtime notification', err);
        }
      };
      ws.onclose = () => {
        if (stopped) return;
        const delay = Math.min(15000, 1000 * 2 ** retry);
        retry += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try {
          ws?.close();
        } catch {
          // ignore
        }
      };
    };

    connect();
    return () => {
      stopped = true;
      cleanup();
    };
  }, [isAuthenticated, token, pushToast, queryClient]);
};
